import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import React from 'react';

export function formatPlaybackClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
}

type Props = {
  currentSeconds: number;
  durationSeconds: number;
  disabled?: boolean;
  /** Fires when the user releases the pointer / keyboard step — commit the seek. */
  onSeek: (seconds: number) => void;
  /** Optional: fires while dragging so the parent can pause/buffer ahead of commit. */
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
};

export function PlaybackVideoScrubber({
  currentSeconds,
  durationSeconds,
  disabled = false,
  onSeek,
  onScrubStart,
  onScrubEnd,
}: Props): React.ReactElement {
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [previewSeconds, setPreviewSeconds] = useState(0);
  const pendingSeekRef = useRef<number | null>(null);

  const duration = Math.max(0.001, durationSeconds);
  const displaySeconds = scrubbing
    ? previewSeconds
    : pendingSeekRef.current ?? Math.min(duration, Math.max(0, currentSeconds));
  const progressPct = duration > 0 ? (displaySeconds / duration) * 100 : 0;

  useEffect(() => {
    if (
      pendingSeekRef.current !== null &&
      !scrubbing &&
      Math.abs(currentSeconds - pendingSeekRef.current) <= 0.6
    ) {
      pendingSeekRef.current = null;
    }
  }, [currentSeconds, scrubbing]);

  const secondsFromClientX = useCallback(
    (clientX: number): number => {
      const track = trackRef.current;
      if (!track) return 0;
      const rect = track.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / width));
      return ratio * duration;
    },
    [duration],
  );

  const clamp = useCallback(
    (seconds: number) => Math.min(duration, Math.max(0, seconds)),
    [duration],
  );

  const commitSeek = useCallback(
    (seconds: number) => {
      const target = clamp(seconds);
      pendingSeekRef.current = target;
      setPreviewSeconds(target);
      onSeek(target);
    },
    [clamp, onSeek],
  );

  const onTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setScrubbing(true);
    setPreviewSeconds(clamp(secondsFromClientX(e.clientX)));
    e.currentTarget.setPointerCapture(e.pointerId);
    onScrubStart?.();
  };

  const onTrackPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!scrubbing || disabled) return;
    e.preventDefault();
    setPreviewSeconds(clamp(secondsFromClientX(e.clientX)));
  };

  const endScrub = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!scrubbing) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setScrubbing(false);
    onScrubEnd?.();
    commitSeek(clamp(secondsFromClientX(e.clientX)));
  };

  return (
    <div
      className={`fb-playback__scrubber${disabled ? ' fb-playback__scrubber--disabled' : ''}`}
      role="group"
      aria-label="Recording playback position"
    >
      <div className="fb-playback__scrubber-times" aria-hidden>
        <span>{formatPlaybackClock(displaySeconds)}</span>
        <span>{formatPlaybackClock(duration)}</span>
      </div>
      <div
        ref={trackRef}
        className="fb-playback__scrubber-track"
        role="slider"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(displaySeconds)}
        aria-valuetext={`${formatPlaybackClock(displaySeconds)} of ${formatPlaybackClock(duration)}`}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onPointerDown={onTrackPointerDown}
        onPointerMove={onTrackPointerMove}
        onPointerUp={endScrub}
        onPointerCancel={endScrub}
        onKeyDown={(e) => {
          if (disabled) return;
          const step = e.shiftKey ? 10 : 5;
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            commitSeek(displaySeconds + step);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            commitSeek(displaySeconds - step);
          } else if (e.key === 'Home') {
            e.preventDefault();
            commitSeek(0);
          } else if (e.key === 'End') {
            e.preventDefault();
            commitSeek(duration);
          }
        }}
      >
        <div className="fb-playback__scrubber-rail" aria-hidden>
          <div
            className="fb-playback__scrubber-fill"
            style={{ width: `${progressPct}%` }}
          />
          <div
            className="fb-playback__scrubber-thumb"
            style={{ left: `${progressPct}%` }}
          />
        </div>
      </div>
    </div>
  );
}
